/**
 * Occupation Edit Page
 * Page for editing existing occupations
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import { useSnackbar } from 'notistack';

// Components
import { OccupationForm, OccupationFormData } from './components';
import { PageShell } from '../../components';

// Services
import { 
  occupationService, 
  Occupation as OccupationFromService,
  SpecialActivityType 
} from '../../services/occupationService';
import { companyService } from '../../services/companyService';
import { departmentService } from '../../services/departmentService';

interface Company {
  id: number;
  tradeName: string;
  legalName: string;
}

interface Department {
  id: number;
  name: string;
  companyId: number;
}

const OccupationEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [occupation, setOccupation] = useState<OccupationFromService | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Companies and departments for form
  const [companies, setCompanies] = useState<Company[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);

  // Load companies and departments
  useEffect(() => {
    const loadData = async () => {
      try {
        const [companiesRes, departmentsRes] = await Promise.all([
          companyService.getCompanies({ page: 1, limit: 1000 }),
          departmentService.getDepartments({ page: 1, limit: 1000 })
        ]);
        
        setCompanies(companiesRes.data.map(c => ({
          id: Number(c.id),
          tradeName: c.tradeName || c.legalName,
          legalName: c.legalName
        })));
        
        setDepartments(departmentsRes.data.map(d => ({
          id: Number(d.id),
          name: d.name,
          companyId: Number(d.companyId)
        })));
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, []);

  // Load occupation data
  useEffect(() => {
    const loadOccupation = async () => {
      if (!id) {
        setError('ID do cargo não fornecido');
        setInitialLoading(false);
        return;
      }

      setInitialLoading(true);
      setError(null);
      
      try {
        console.log(`💼 Loading occupation ${id}...`);
        
        const data = await occupationService.getOccupationById(Number(id));
        
        console.log('✅ Occupation loaded:', data);
        setOccupation(data);
      } catch (err: unknown) {
        console.error('❌ Error loading occupation:', err);
        const apiError = err as { response?: { data?: { message?: string } }; message?: string };
        const errorMessage = (apiError.response?.data?.message || apiError.message) ?? 'Erro ao carregar cargo';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setInitialLoading(false);
      }
    };

    loadOccupation();
  }, [id, enqueueSnackbar]);

  // Handle form submission
  const handleSubmit = async (data: OccupationFormData) => {
    if (!id) return;
    
    setLoading(true);
    
    try {
      console.log('💼 Updating occupation:', id, data);
      
      // Validate departmentId and companyId
      if (!data.departmentId || !data.companyId) {
        enqueueSnackbar('Empresa e Departamento são obrigatórios', { variant: 'error' });
        return;
      }
      
      // Convert specialActivities object to array
      const specialActivitiesArray: SpecialActivityType[] = [];
      if (data.specialActivities) {
        Object.entries(data.specialActivities).forEach(([key, value]) => {
          if (value === true) {
            specialActivitiesArray.push(key as SpecialActivityType);
          }
        });
      }
      
      const requestData = {
        name: data.name.trim(),
        cboCode: data.cboCode?.trim() ?? undefined,
        description: data.description?.trim() ?? undefined,
        role: data.role?.trim() ?? undefined,
        departmentId: Number(data.departmentId),
        companyId: Number(data.companyId),
        specialActivities: specialActivitiesArray,
      };
      
      console.log('📤 Sending to API:', requestData);
      
      await occupationService.updateOccupation(Number(id), requestData);
      
      console.log('✅ Occupation updated successfully');
      enqueueSnackbar('Cargo atualizado com sucesso!', { variant: 'success' });
      navigate('/occupations');
    } catch (error: unknown) {
      console.error('❌ Error updating occupation:', error);
      const apiError = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = (apiError.response?.data?.message || apiError.message) ?? 'Erro ao atualizar cargo';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/occupations');
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Cargos', path: '/occupations' },
    { label: 'Editar Cargo' },
  ];

  // Loading state
  if (initialLoading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Cargo">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  // Error state
  if (error || !occupation) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Cargo">
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Cargo não encontrado'}
        </Alert>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/occupations')}
            sx={{ textDecoration: 'none' }}
          >
            ← Voltar para lista de cargos
          </Link>
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title="Editar Cargo"
      subtitle={`Atualize as informações do cargo: ${occupation.name}`}
    >
      {/* Form Card */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <OccupationForm
            mode="edit"
            occupation={occupation}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
            companies={companies}
            departments={departments}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default OccupationEditPage;
